import NiceModal from '@ebay/nice-modal-react';

import { useMemo } from 'react';

import { NonFormContainer } from 'components/form/FormLayout.styled';
import Authorization from 'components/global/Authorization';
import PageHeader from 'components/global/PageHeader';
import SubformHeader from 'components/global/SubformHeader';
import Typography from 'components/global/Typography';
import { TabTableContainer } from 'components/layout/AppLayout/AppLayout.styled';
import DynamicList from 'components/list/DynamicList';
import { FilterType, ListComponents } from 'components/list/DynamicListFilterUtils';

import ImportDataModal from 'modals/ImportDataModal';

import { IMPORT_EXPORT, PRICE_CONFIGURATIONS, SHIPPING_PARTNERS } from 'services/consts';

import { DYNAMICLISTS } from 'utils/constants';

import {
	faArrowUpWideShort,
	faBallotCheck,
	faBoxOpen,
	faCabinetFiling,
	faCar,
	faCartFlatbedBoxes,
	faFileInvoice,
	faPeopleGroup,
	faStore,
	faUsers,
	faWarehouseFull
} from '@fortawesome/pro-regular-svg-icons';

import CustomOrderImport from '../CustomOrderImport';
import * as S from '../ImportExport.styled';

/**
 * @author @muhammedburakak
 */

const ImportLogList = () => {
	const defaultSort = useMemo(() => [{ id: 'ID', desc: true }], []);

	const columns = useMemo(
		() => [
			{ Header: 'id', accessor: 'ID', width: 60, filterType: FilterType.number, show: false },
			{
				Header: 'importType',
				accessor: 'type',
				width: 70,
				filterType: FilterType.multiSelection,
				showOnMobile: true
			},
			{ Header: 'failedRecords', accessor: 'failCount', width: 50, filterType: FilterType.number },
			{
				Header: 'successRecords',
				accessor: 'successCount',
				width: 50,
				filterType: FilterType.number
			},
			{ Header: 'totalRecords', accessor: 'recordCount', width: 50, filterType: FilterType.number },
			{
				Header: 'completedDate',
				accessor: 'completedAt',
				width: 110,
				filterType: FilterType.date,
				renderCell: ListComponents.DATETIME
			},
			{
				Header: 'description',
				accessor: 'description',
				width: 110,
				filterType: FilterType.string
			},
			{
				Header: 'resultFile',
				accessor: 'fileOutput',
				width: 110,
				filterType: FilterType.string,
				renderCell: ListComponents.DOWNLOAD_IMPORT_FILE_BUTTON,
				showOnMobile: true
			},
			{
				Header: 'status',
				accessor: 'status',
				width: 80,
				filterType: FilterType.multiSelection,
				renderCell: ListComponents.IMPORT_FILE_STATUS,
				showOnMobile: true
			}
		],
		[]
	);

	return (
		<TabTableContainer>
			<PageHeader
				lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/Import-New.json.gz"
				title="importLogs"
			/>
			<NonFormContainer
				style={{ borderRadius: 'var(--small-border-radius)', padding: 'var(--padding-2)' }}
				className="mb-5">
				<SubformHeader lightTitle="importSubtitle" className="py-1" />
				<ImportActions />
			</NonFormContainer>

			<DynamicList
				id={DYNAMICLISTS.IMPORT_LOGS}
				columns={columns}
				fetchUrl={IMPORT_EXPORT.GET_IMPORT_LOG_LIST}
				defaultSort={defaultSort}
				exportName="importData"
			/>
		</TabTableContainer>
	);
};

// Components
const ImportActionButton = ({ action, modalTrigger }) => (
	<div className="col">
		<S.ImportActionButtonStyled onClick={() => modalTrigger(action)} variant="borderless" fullWidth size="large">
			<S.BoxIcon icon={action.icon} size="small" />
			<Typography fontWeight="700" noWrap>
				{action.title}
			</Typography>
		</S.ImportActionButtonStyled>
	</div>
);

// actions to use application scope
export const importActions = {
	productImport: {
		icon: faBoxOpen,
		title: 'productImport',
		role: 'productImport',
		downloadUrl: IMPORT_EXPORT.GET_PRODUCT_TEMPLATE,
		uploadUrl: IMPORT_EXPORT.IMPORT_PRODUCT,
		acceptedFileTypes: ['.xlsx', '.xls']
	},
	categoryImport: {
		icon: faBoxOpen,
		title: 'categoryImport',
		role: 'categoryImport',
		downloadUrl: IMPORT_EXPORT.GET_CATEGORY_TEMPLATE,
		uploadUrl: IMPORT_EXPORT.IMPORT_CATEGORIES,
		acceptedFileTypes: ['.xlsx', '.xls']
	},
	orderImport: {
		icon: faCartFlatbedBoxes,
		title: 'orderImport',
		role: 'orderImport',
		downloadUrl: IMPORT_EXPORT.GET_ORDER_TEMPLATE,
		uploadUrl: IMPORT_EXPORT.IMPORT_ORDER,
		acceptedFileTypes: ['.xlsx', '.xls']
	},
	warehouseImport: {
		icon: faWarehouseFull,
		title: 'warehouseImport',
		role: 'warehouseImport',
		downloadUrl: IMPORT_EXPORT.GET_WAREHOUSE_TEMPLATE,
		uploadUrl: IMPORT_EXPORT.IMPORT_WAREHOUSE,
		acceptedFileTypes: ['.xlsx', '.xls']
	},
	branchImport: {
		icon: faStore,
		title: 'branchImport',
		role: 'branchImport',
		downloadUrl: IMPORT_EXPORT.GET_BRANCH_TEMPLATE,
		uploadUrl: IMPORT_EXPORT.IMPORT_BRANCH,
		acceptedFileTypes: ['.xlsx', '.xls']
	},
	driverImport: {
		icon: faCar,
		title: 'driverImport',
		role: 'driverImport',
		downloadUrl: IMPORT_EXPORT.GET_DRIVER_TEMPLATE,
		uploadUrl: IMPORT_EXPORT.IMPORT_DRIVER,
		acceptedFileTypes: ['.xlsx', '.xls']
	},
	userImport: {
		icon: faUsers,
		title: 'userImport',
		role: 'userImport',
		downloadUrl: IMPORT_EXPORT.GET_USER_TEMPLATE,
		uploadUrl: IMPORT_EXPORT.IMPORT_USER,
		acceptedFileTypes: ['.xlsx', '.xls']
	},
	customerListImport: {
		icon: faPeopleGroup,
		title: 'customerListImport',
		role: 'customerListImport',
		downloadUrl: IMPORT_EXPORT.GET_CUSTOMER_LIST_TEMPLATE,
		uploadUrl: IMPORT_EXPORT.IMPORT_CUSTOMER_LIST,
		acceptedFileTypes: ['.xlsx', '.xls']
	},
	inventoryImport: {
		icon: faBallotCheck,
		title: 'inventoryImport',
		role: 'inventoryImport',
		downloadUrl: IMPORT_EXPORT.GET_INVENTORY_TEMPLATE,
		uploadUrl: IMPORT_EXPORT.IMPORT_INVENTORY,
		acceptedFileTypes: ['.xlsx', '.xls']
	},
	binInventoryImport: {
		icon: faCabinetFiling,
		title: 'binInventoryTitle',
		role: 'binInventoryImport',
		downloadUrl: IMPORT_EXPORT.GET_BIN_INVENTORY_TEMPLATE,
		uploadUrl: IMPORT_EXPORT.IMPORT_BIN_INVENTORY,
		acceptedFileTypes: ['.xlsx', '.xls']
	},
	binImport: {
		icon: faCabinetFiling,
		title: 'binTitle',
		role: 'binImport',
		downloadUrl: IMPORT_EXPORT.GET_BIN_TEMPLATE,
		uploadUrl: IMPORT_EXPORT.IMPORT_BIN,
		acceptedFileTypes: ['.xlsx', '.xls']
	},
	zoneImport: {
		icon: faCabinetFiling,
		title: 'zoneTitle',
		role: 'zoneImport',
		downloadUrl: IMPORT_EXPORT.GET_ZONE_TEMPLATE,
		uploadUrl: IMPORT_EXPORT.IMPORT_ZONE,
		acceptedFileTypes: ['.xlsx', '.xls']
	},
	invoiceImport: {
		icon: faFileInvoice,
		title: 'invoiceImport',
		role: 'invoiceImport',
		downloadUrl: IMPORT_EXPORT.GET_INVOICE_TEMPLATE,
		uploadUrl: IMPORT_EXPORT.IMPORT_INVOICE,
		acceptedFileTypes: ['.xlsx', '.xls']
	},
	shippingDescriptionImport: {
		icon: faArrowUpWideShort,
		title: 'shippingDescriptionImport',
		role: 'shippingDescriptionImport',
		downloadUrl: IMPORT_EXPORT.GET_SHIPPING_DESCRIPTION_TEMPLATE,
		uploadUrl: IMPORT_EXPORT.IMPORT_SHIPPING_DESCRIPTION,
		acceptedFileTypes: ['.xlsx', '.xls']
	},
	// Price Configurations
	shippingContractImport: {
		icon: faArrowUpWideShort,
		title: 'shippingContractImport',
		role: 'shippingContractImport',
		downloadUrl: PRICE_CONFIGURATIONS.GET_SHIPPING_CONTRACT_TEMPLATE,
		uploadUrl: PRICE_CONFIGURATIONS.IMPORT_SHIPPING_CONTRACT_IMPORT,
		acceptedFileTypes: ['.xlsx', '.xls']
	},
	otoDeliveryPriceImport: {
		icon: faArrowUpWideShort,
		title: 'otoDeliveryPriceImport',
		role: 'otoDeliveryPriceImport',
		downloadUrl: PRICE_CONFIGURATIONS.GET_OTO_DELIVERY_PRICE_TEMPLATE,
		uploadUrl: PRICE_CONFIGURATIONS.IMPORT_OTO_DELIVERY_PRICE_IMPORT,
		acceptedFileTypes: ['.xlsx', '.xls']
	},
	successInstructionsImport: {
		icon: faArrowUpWideShort,
		title: 'successInstructionsImport',
		role: 'successInstructionsImport',
		downloadUrl: PRICE_CONFIGURATIONS.GET_SUCCESS_INSTRUCTION_TEMPLATE,
		uploadUrl: PRICE_CONFIGURATIONS.IMPORT_SUCCESS_INSTRUCTION,
		acceptedFileTypes: ['.xlsx', '.xls']
	},
	otoMarginImport: {
		icon: faArrowUpWideShort,
		title: 'otoMarginImport',
		role: 'otoMarginImport',
		downloadUrl: PRICE_CONFIGURATIONS.GET_OTO_MARGIN_PRICE_TEMPLATE,
		uploadUrl: PRICE_CONFIGURATIONS.IMPORT_OTO_MARGIN_PRICE,
		acceptedFileTypes: ['.xlsx', '.xls']
	},
	specialContractTermImport: {
		icon: faArrowUpWideShort,
		title: 'specialContractTermImport',
		role: 'specialContractTermImport',
		downloadUrl: PRICE_CONFIGURATIONS.GET_SPECIAL_CONTRACT_TERM_TEMPLATE,
		uploadUrl: PRICE_CONFIGURATIONS.IMPORT_SPECIAL_CONTRACT_TERM_IMPORT,
		acceptedFileTypes: ['.xlsx', '.xls']
	},
	tierBasedPriceImport: {
		icon: faArrowUpWideShort,
		title: 'tierBasedPriceImport',
		role: 'tierBasedPriceImport',
		downloadUrl: PRICE_CONFIGURATIONS.GET_TIER_BASED_PRICE_TEMPLATE,
		uploadUrl: PRICE_CONFIGURATIONS.IMPORT_TIER_BASED_PRICE_IMPORT,
		acceptedFileTypes: ['.xlsx', '.xls']
	},
	cityBasedPriceImport: {
		icon: faArrowUpWideShort,
		title: 'cityBasedPriceImport',
		role: 'cityBasedPriceImport',
		downloadUrl: PRICE_CONFIGURATIONS.GET_CITY_BASED_PRICE_TEMPLATE,
		uploadUrl: PRICE_CONFIGURATIONS.IMPORT_CITY_BASED_PRICE_IMPORT,
		acceptedFileTypes: ['.xlsx', '.xls']
	},
	kmBasedPriceImport: {
		icon: faArrowUpWideShort,
		title: 'kmBasedPriceImport',
		role: 'kmBasedPriceImport',
		downloadUrl: PRICE_CONFIGURATIONS.GET_KM_BASED_PRICE_TEMPLATE,
		uploadUrl: PRICE_CONFIGURATIONS.IMPORT_KM_BASED_PRICE_IMPORT,
		acceptedFileTypes: ['.xlsx', '.xls']
	},
	weightBasedPriceImport: {
		icon: faArrowUpWideShort,
		title: 'weightBasedPriceImport',
		role: 'weightBasedPriceImport',
		downloadUrl: PRICE_CONFIGURATIONS.GET_WEIGHT_BASED_PRICE_TEMPLATE,
		uploadUrl: PRICE_CONFIGURATIONS.IMPORT_WEIGHT_BASED_PRICE_IMPORT,
		acceptedFileTypes: ['.xlsx', '.xls']
	},
	weightCountryBasedPriceImport: {
		icon: faArrowUpWideShort,
		title: 'weightCountryBasedPriceImport',
		role: 'weightCountryBasedPriceImport',
		downloadUrl: PRICE_CONFIGURATIONS.GET_WEIGHT_COUNTRY_BASED_PRICE_TEMPLATE,
		uploadUrl: PRICE_CONFIGURATIONS.IMPORT_WEIGHT_COUNTRY_BASED_PRICE_IMPORT,
		acceptedFileTypes: ['.xlsx', '.xls']
	},
	tierBasedSLAImport: {
		icon: faArrowUpWideShort,
		title: 'tierBasedSLAImport',
		role: 'tierBasedSLAImport',
		downloadUrl: PRICE_CONFIGURATIONS.GET_TIER_BASED_SLA_TEMPLATE,
		uploadUrl: PRICE_CONFIGURATIONS.IMPORT_TIER_BASED_SLA_IMPORT,
		acceptedFileTypes: ['.xlsx', '.xls']
	},
	kmBasedSLAImport: {
		icon: faArrowUpWideShort,
		title: 'kmBasedSLAImport',
		role: 'kmBasedSLAImport',
		downloadUrl: PRICE_CONFIGURATIONS.GET_KM_BASED_SLA_TEMPLATE,
		uploadUrl: PRICE_CONFIGURATIONS.IMPORT_KM_BASED_SLA_IMPORT,
		acceptedFileTypes: ['.xlsx', '.xls']
	},
	bulletKMSLAImport: {
		icon: faArrowUpWideShort,
		title: 'bulletKMSLAImport',
		role: 'bulletKMSLAImport',
		downloadUrl: PRICE_CONFIGURATIONS.GET_BULLET_KM_SLA_TEMPLATE,
		uploadUrl: PRICE_CONFIGURATIONS.IMPORT_BULLET_KM_SLA_IMPORT,
		acceptedFileTypes: ['.xlsx', '.xls']
	},
	cityBasedSLAImport: {
		icon: faArrowUpWideShort,
		title: 'cityBasedSLAImport',
		role: 'cityBasedSLAImport',
		downloadUrl: PRICE_CONFIGURATIONS.GET_CITY_BASED_SLA_TEMPLATE,
		uploadUrl: PRICE_CONFIGURATIONS.IMPORT_CITY_BASED_SLA_IMPORT,
		acceptedFileTypes: ['.xlsx', '.xls']
	},
	districtBasedSLAImport: {
		icon: faArrowUpWideShort,
		title: 'districtBasedSLAImport',
		role: 'districtBasedSLAImport',
		downloadUrl: PRICE_CONFIGURATIONS.GET_DISTRICT_BASED_SLA_TEMPLATE,
		uploadUrl: PRICE_CONFIGURATIONS.IMPORT_DISTRICT_BASED_SLA_IMPORT,
		acceptedFileTypes: ['.xlsx', '.xls']
	},
	countryBasedSLAImport: {
		icon: faArrowUpWideShort,
		title: 'countryBasedSLAImport',
		role: 'countryBasedSLAImport',
		downloadUrl: PRICE_CONFIGURATIONS.GET_COUNTRY_BASED_SLA_TEMPLATE,
		uploadUrl: PRICE_CONFIGURATIONS.IMPORT_COUNTRY_BASED_SLA_IMPORT,
		acceptedFileTypes: ['.xlsx', '.xls']
	},
	tierBasedSLAImportForCompany: {
		icon: faArrowUpWideShort,
		title: 'tierBasedSLAImportForCompany',
		role: 'tierBasedSLAImportForCompany',
		downloadUrl: SHIPPING_PARTNERS.GET_TIER_BASED_SLA_TEMPLATE_FOR_COMPANY,
		uploadUrl: SHIPPING_PARTNERS.IMPORT_TIER_BASED_SLA_FOR_COMPANY,
		acceptedFileTypes: ['.xlsx', '.xls']
	},
	kmBasedPriceImportForCompany: {
		icon: faArrowUpWideShort,
		title: 'kmBasedPriceImportForCompany',
		role: 'kmBasedPriceImportForCompany',
		downloadUrl: SHIPPING_PARTNERS.GET_KM_BASED_SLA_TEMPLATE_FOR_COMPANY,
		uploadUrl: SHIPPING_PARTNERS.IMPORT_KM_BASED_SLA_FOR_COMPANY,
		acceptedFileTypes: ['.xlsx', '.xls']
	},
	countryBasedSLAImportForCompany: {
		icon: faArrowUpWideShort,
		title: 'countryBasedSLAImportForCompany',
		role: 'countryBasedSLAImportForCompany',
		downloadUrl: SHIPPING_PARTNERS.GET_COUNTRY_BASED_SLA_TEMPLATE_FOR_COMPANY,
		uploadUrl: SHIPPING_PARTNERS.IMPORT_COUNTRY_BASED_SLA_FOR_COMPANY,
		acceptedFileTypes: ['.xlsx', '.xls']
	},
	cityBasedPriceImportForCompany: {
		icon: faArrowUpWideShort,
		title: 'cityBasedPriceImportForCompany',
		role: 'cityBasedPriceImportForCompany',
		downloadUrl: SHIPPING_PARTNERS.GET_CITY_BASED_SLA_TEMPLATE_FOR_COMPANY,
		uploadUrl: SHIPPING_PARTNERS.IMPORT_CITY_BASED_SLA_FOR_COMPANY,
		acceptedFileTypes: ['.xlsx', '.xls']
	},
	districtBasedSLAImportForCompany: {
		icon: faArrowUpWideShort,
		title: 'districtBasedSLAImportForCompany',
		role: 'districtBasedSLAImportForCompany',
		downloadUrl: SHIPPING_PARTNERS.GET_DISTRICT_BASED_SLA_TEMPLATE_FOR_COMPANY,
		uploadUrl: SHIPPING_PARTNERS.IMPORT_DISTRICT_BASED_SLA_FOR_COMPANY,
		acceptedFileTypes: ['.xlsx', '.xls']
	},
	bulletKMSLAImportForCompany: {
		icon: faArrowUpWideShort,
		title: 'bulletKMSLAImportForCompany',
		role: 'bulletKMSLAImportForCompany',
		downloadUrl: SHIPPING_PARTNERS.GET_BULLET_KM_SLA_TEMPLATE_FOR_COMPANY,
		uploadUrl: SHIPPING_PARTNERS.IMPORT_BULLET_KM_SLA_FOR_COMPANY,
		acceptedFileTypes: ['.xlsx', '.xls']
	},
	bulletDistrictSLAImportForCompany: {
		icon: faArrowUpWideShort,
		title: 'bulletDistrictSLAImportForCompany',
		role: 'bulletDistrictSLAImportForCompany',
		downloadUrl: SHIPPING_PARTNERS.GET_BULLET_DISTRICT_SLA_TEMPLATE_FOR_COMPANY,
		uploadUrl: SHIPPING_PARTNERS.IMPORT_BULLET_DISTRICT_SLA_FOR_COMPANY,
		acceptedFileTypes: ['.xlsx', '.xls']
	},
	invoiceCheckerDeliveryCompanyImport: {
		icon: faArrowUpWideShort,
		title: 'invoiceCheckerImport',
		role: 'invoiceCheckerImport',
		downloadUrl: PRICE_CONFIGURATIONS.GET_INVOICE_CHECKER_DELIVERY_COMPANY_TEMPLATE,
		uploadUrl: PRICE_CONFIGURATIONS.IMPORT_INVOICE_CHECKER_DELIVERY_COMPANY,
		acceptedFileTypes: ['.xlsx', '.xls']
	},
	invoiceCheckerDailyWeightImport: {
		icon: faArrowUpWideShort,
		title: 'dailyWeightChargeImport',
		role: 'dailyWeightChargeImport',
		downloadUrl: PRICE_CONFIGURATIONS.GET_INVOICE_CHECKER_DAILY_WEIGHT_TEMPLATE,
		uploadUrl: PRICE_CONFIGURATIONS.IMPORT_INVOICE_CHECKER_DAILY_WEIGHT,
		acceptedFileTypes: ['.xlsx', '.xls']
	},
	receivableReportsImport: {
		icon: faArrowUpWideShort,
		title: 'receivableReportsImport',
		role: 'receivableReportsImport',
		downloadUrl: IMPORT_EXPORT.GET_RECEIVABLE_REPORTS_TEMPLATE,
		uploadUrl: IMPORT_EXPORT.IMPORT_RECEIVABLE_REPORTS,
		acceptedFileTypes: ['.xlsx', '.xls']
	},
	sallaOrdersImport: {
		icon: faArrowUpWideShort,
		title: 'sallaOrdersImport',
		role: 'sallaOrdersImport',
		instruction: 'sallaOrdersImportInstruction',
		uploadUrl: IMPORT_EXPORT.IMPORT_SALLA_ORDERS,
		acceptedFileTypes: ['.xlsx', '.xls']
	},
	customOrdersImport: {
		icon: faArrowUpWideShort,
		title: 'customOrdersImport',
		role: 'customOrdersImport',
		renderCustomComponent: CustomOrderImport,
		uploadUrl: IMPORT_EXPORT.IMPORT_CUSTOM_ORDERS,
		acceptedFileTypes: ['.xlsx', '.xls']
	},
	ksaOrdersImport: {
		icon: faArrowUpWideShort,
		title: 'ksaOrdersImport',
		role: 'ksaOrdersImport',
		uploadUrl: IMPORT_EXPORT.IMPORT_KSA_ORDERS,
		acceptedFileTypes: ['.xlsx', '.xls']
	}
};
// actions to use application scope

const ImportActions = () => {
	const handleModalTrigger = (action) => {
		const { title, acceptedFileTypes, downloadUrl, uploadUrl, instruction, renderCustomComponent } = action;
		NiceModal.show(ImportDataModal, {
			title,
			acceptedFileTypes,
			downloadUrl,
			uploadUrl,
			instruction,
			renderCustomComponent
		});
	};

	return (
		<S.StyledButtonContainer className="row row-cols-2 row-cols-md-3 row-cols-xxl-6">
			{Object.keys(importActions).map((action, i) => (
				<Authorization key={i} path={`/import-export/button/${importActions[action].role}`}>
					<ImportActionButton action={importActions[action]} modalTrigger={handleModalTrigger} />
				</Authorization>
			))}
		</S.StyledButtonContainer>
	);
};

export default ImportLogList;
